import { useEffect, useMemo, useState, useCallback } from 'react';
import { kbArticleListResponseSchema, kbArticleResponseSchema, kbArticlePatchSchema, type KbArticle } from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Alert } from '@easyflow/ui-frontend/components/ui/alert';
import { usePermissions } from '@/contexts/PermissionContext';
import apiClient, { isApiError } from '@/lib/api';

const CATEGORY = 'calendar_appointments';

function renderMarkdownLite(md: string): string {
  const esc = (s: string) => s.replace(/[&<>]/g, c => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as Record<string, string>)[c] ?? c));
  const lines = md.split('\n');
  let html = '';
  let inUl = false;
  let inTable = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith('### ')) { if (inUl) { html += '</ul>'; inUl = false; } html += `<h3 class="mt-4 text-base font-semibold">${esc(line.slice(4))}</h3>`; continue; }
    if (line.startsWith('## ')) { if (inUl) { html += '</ul>'; inUl = false; } html += `<h2 class="mt-5 text-lg font-semibold">${esc(line.slice(3))}</h2>`; continue; }
    if (line.startsWith('# ')) { if (inUl) { html += '</ul>'; inUl = false; } html += `<h1 class="mt-6 text-xl font-bold">${esc(line.slice(2))}</h1>`; continue; }
    if (line.startsWith('|')) {
      const cells = line.slice(1, line.endsWith('|') ? -1 : undefined).split('|').map(c => c.trim());
      if (/^[-:\s|]+$/.test(line)) continue;
      if (!inTable) { html += '<table class="my-3 min-w-full border-collapse text-sm"><tbody>'; inTable = true; }
      html += '<tr>' + cells.map(c => `<td class="border border-border px-2 py-1">${esc(c).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</td>`).join('') + '</tr>';
      continue;
    } else if (inTable) { html += '</tbody></table>'; inTable = false; }
    if (line.startsWith('- ') || line.startsWith('• ')) {
      if (!inUl) { html += '<ul class="ml-5 list-disc space-y-1">'; inUl = true; }
      html += `<li>${esc(line.slice(2)).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/`(.+?)`/g, '<code class="rounded bg-muted px-1">$1</code>')}</li>`;
      continue;
    }
    if (inUl) { html += '</ul>'; inUl = false; }
    if (!line) { html += ''; continue; }
    html += `<p class="mt-2 text-sm leading-6">${esc(line).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/`(.+?)`/g, '<code class="rounded bg-muted px-1">$1</code>')}</p>`;
  }
  if (inUl) html += '</ul>';
  if (inTable) html += '</tbody></table>';
  return html;
}

export default function KnowledgeBasePage() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canRead = hasPermission('calendar.view');
  const canWrite = hasPermission('ai_tools.manage');
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDenied(false);
    try {
      const response = await apiClient.get<unknown>(`/kb/${CATEGORY}`);
      if (!response.success) throw new Error(response.error || 'Caricamento della guida non riuscito');
      const parsed = kbArticleListResponseSchema.safeParse(response);
      if (!parsed.success) throw new Error('Risposta della guida non valida');
      setArticles(parsed.data.data);
      setActiveSlug(current => parsed.data.data.some(article => article.slug === current)
        ? current : parsed.data.data[0]?.slug ?? null);
    } catch (err: unknown) {
      setArticles([]);
      setDenied(isApiError(err) && err.status === 403);
      setError(err instanceof Error ? err.message : 'Caricamento della guida non riuscito');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (permissionsLoading) return;
    if (canRead) void load();
    else { setArticles([]); setLoading(false); }
  }, [permissionsLoading, canRead, load]);

  const active = useMemo(() => articles.find(a => a.slug === activeSlug) || null, [articles, activeSlug]);

  const startEdit = () => {
    if (!active || !canWrite) return;
    setEditTitle(active.title);
    setEditSummary(active.summary || '');
    setEditContent(active.content);
    setEditing(true);
  };

  const save = async () => {
    if (!active || !canWrite) return;
    setBusy(true);
    setError(null);
    try {
      const patch = kbArticlePatchSchema.parse({
        title: editTitle, summary: editSummary, content: editContent,
      });
      const response = await apiClient.put<unknown>(`/kb/${CATEGORY}/${active.slug}`, patch);
      if (!response.success) throw new Error(response.error || 'Salvataggio non riuscito');
      if (!kbArticleResponseSchema.safeParse(response).success) throw new Error('Risposta articolo non valida');
      await load();
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito');
    } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!active || !canWrite) return;
    if (!confirm('Ripristinare il contenuto di default?')) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiClient.post<unknown>(`/kb/${CATEGORY}/${active.slug}/restore`);
      if (!response.success) throw new Error(response.error || 'Ripristino non riuscito');
      if (!kbArticleResponseSchema.safeParse(response).success) throw new Error('Risposta articolo non valida');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ripristino non riuscito');
    } finally { setBusy(false); }
  };

  if (permissionsLoading) return <div role="status" className="p-4">Verifica dei permessi…</div>;
  if (!canRead || denied) return <Alert variant="destructive">Accesso negato alla guida Calendario: è richiesto il permesso calendar.view.</Alert>;
  if (loading) return <div role="status" className="p-4">Caricamento della guida Calendario…</div>;

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-12rem)] gap-4 p-2 sm:p-4">
      <aside className="w-full lg:w-72 lg:shrink-0 max-h-64 lg:max-h-none overflow-y-auto rounded-lg border border-border bg-card">
        <div className="border-b border-border p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Knowledge base</div>
          <div className="text-sm font-semibold">Calendario & Appuntamenti</div>
        </div>
        <ul>
          {articles.map(a => (
            <li key={a.slug}>
              <Button
                variant="ghost"
                onClick={() => { setActiveSlug(a.slug); setEditing(false); }}
                className={`block h-auto w-full whitespace-normal px-3 py-2 text-left text-sm ${
                  activeSlug === a.slug ? 'bg-accent font-semibold text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <div>{a.title}</div>
                {a.summary && <div className="mt-0.5 text-xs text-muted-foreground">{a.summary.slice(0, 80)}{a.summary.length > 80 ? '…' : ''}</div>}
              </Button>
            </li>
          ))}
        </ul>
      </aside>
      <main className="flex-1 overflow-y-auto rounded-lg border border-border bg-card p-3 sm:p-5">
        {error && <Alert variant="destructive">{error}<Button variant="outline" onClick={() => void load()}>Riprova</Button></Alert>}
        {!active && !error && <div className="text-sm text-muted-foreground">Nessun capitolo disponibile</div>}
        {active && !editing && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <h1 className="text-xl font-bold">{active.title}</h1>
              {canWrite && <div className="flex items-center gap-2">
                {!active.is_default && (
                  <Button variant="outline" onClick={restore} disabled={busy}>
                    Ripristina default
                  </Button>
                )}
                <Button onClick={startEdit} disabled={busy}>
                  Modifica
                </Button>
              </div>}
            </div>
            {active.summary && <p className="mb-3 text-sm italic text-muted-foreground">{active.summary}</p>}
            <article className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdownLite(active.content) }} />
          </>
        )}
        {active && editing && canWrite && (
          <div className="space-y-3">
            <Input
              aria-label="Titolo"
              value={editTitle} onChange={e => setEditTitle(e.target.value)}
              className="w-full rounded border border-border p-2 text-base font-semibold"
            />
            <Input
              aria-label="Sommario"
              value={editSummary} onChange={e => setEditSummary(e.target.value)}
              placeholder="Sommario"
              className="w-full rounded border border-border p-2 text-sm"
            />
            <Textarea
              aria-label="Contenuto"
              value={editContent} onChange={e => setEditContent(e.target.value)}
              rows={28}
              className="w-full rounded border border-border p-2 font-mono text-xs"
            />
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy}>
                Salva
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>
                Annulla
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
